import dayjs from 'dayjs';

/**
 * Interface for fanbase chart data points
 */
export interface MockFanbaseChartData {
  datetime: string;
  fanbase_count: number;
}

/**
 * Interface for weekly growth data
 */
export interface MockWeeklyGrowth {
  currentGrowthValue: number;
  difference: number;
  percentage: number;
}

/**
 * Interface for complete fanbase data
 */
export interface MockFanbaseData {
  weeklyChartData: MockFanbaseChartData[];
  weeklyGrowth: MockWeeklyGrowth;
}

/**
 * Generate realistic fanbase chart data for a given date range
 * @param startDate - Start date for the data range
 * @param endDate - End date for the data range
 * @param baseFollowers - Base number of followers to start with
 * @param growthTrend - Growth trend: 'growing', 'declining', 'stable', 'volatile'
 * @returns Array of chart data points
 */
const generateChartData = (
  startDate: string,
  endDate: string,
  baseFollowers: number,
  growthTrend: 'growing' | 'declining' | 'stable' | 'volatile' = 'growing'
): MockFanbaseChartData[] => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const days = end.diff(start, 'day');
  const data: MockFanbaseChartData[] = [];
  
  let currentFollowers = baseFollowers;
  
  for (let i = 0; i <= days; i++) {
    const date = start.add(i, 'day').format('YYYY-MM-DD');
    
    // Generate realistic daily growth based on trend
    let dailyChange = 0;
    
    switch (growthTrend) {
      case 'growing':
        // Steady growth with some randomness (0.1% to 2% daily growth)
        dailyChange = Math.floor(currentFollowers * (0.001 + Math.random() * 0.019));
        break;
      case 'declining':
        // Gradual decline (-0.5% to -0.1% daily)
        dailyChange = -Math.floor(currentFollowers * (0.001 + Math.random() * 0.004));
        break;
      case 'stable':
        // Very small changes (-0.2% to +0.2%)
        dailyChange = Math.floor(currentFollowers * (Math.random() * 0.004 - 0.002));
        break;
      case 'volatile':
        // Large swings (-1% to +3%)
        dailyChange = Math.floor(currentFollowers * (Math.random() * 0.04 - 0.01));
        break;
    }
    
    // Add some weekend/weekday patterns (higher growth on weekends)
    const dayOfWeek = start.add(i, 'day').day();
    if (dayOfWeek === 0 || dayOfWeek === 6) { // Weekend
      dailyChange = Math.floor(dailyChange * 1.3);
    }
    
    currentFollowers = Math.max(0, currentFollowers + dailyChange);
    
    data.push({
      datetime: date,
      fanbase_count: currentFollowers
    });
  }
  
  return data;
};

/**
 * Calculate weekly growth from chart data
 * @param chartData - Array of chart data points
 * @returns Weekly growth object
 */
const calculateWeeklyGrowth = (chartData: MockFanbaseChartData[]): MockWeeklyGrowth => {
  if (chartData.length < 2) {
    return {
      currentGrowthValue: 0,
      difference: 0,
      percentage: 0
    };
  }
  
  const currentValue = chartData[chartData.length - 1].fanbase_count;
  const previousValue = chartData[0].fanbase_count;
  const difference = currentValue - previousValue;
  const percentage = previousValue > 0 ? (difference / previousValue) * 100 : 0;
  
  return {
    currentGrowthValue: currentValue,
    difference,
    percentage: Math.round(percentage * 10) / 10 // Round to 1 decimal place
  };
};

/**
 * Mock fanbase data scenarios for different brand performance types
 */
export const MOCK_FANBASE_SCENARIOS = {
  highPerformer: (startDate: string, endDate: string): MockFanbaseData => {
    const chartData = generateChartData(startDate, endDate, 45000, 'growing');
    return {
      weeklyChartData: chartData,
      weeklyGrowth: calculateWeeklyGrowth(chartData)
    };
  },
  
  steadyGrower: (startDate: string, endDate: string): MockFanbaseData => {
    const chartData = generateChartData(startDate, endDate, 28000, 'growing');
    return {
      weeklyChartData: chartData,
      weeklyGrowth: calculateWeeklyGrowth(chartData)
    };
  },
  
  stableBrand: (startDate: string, endDate: string): MockFanbaseData => {
    const chartData = generateChartData(startDate, endDate, 18500, 'stable');
    return {
      weeklyChartData: chartData,
      weeklyGrowth: calculateWeeklyGrowth(chartData)
    };
  },
  
  volatileBrand: (startDate: string, endDate: string): MockFanbaseData => {
    const chartData = generateChartData(startDate, endDate, 32000, 'volatile');
    return {
      weeklyChartData: chartData,
      weeklyGrowth: calculateWeeklyGrowth(chartData)
    };
  },
  
  decliningBrand: (startDate: string, endDate: string): MockFanbaseData => {
    const chartData = generateChartData(startDate, endDate, 25000, 'declining');
    return {
      weeklyChartData: chartData,
      weeklyGrowth: calculateWeeklyGrowth(chartData)
    };
  },
  
  newBrand: (startDate: string, endDate: string): MockFanbaseData => {
    const chartData = generateChartData(startDate, endDate, 1200, 'growing');
    return {
      weeklyChartData: chartData,
      weeklyGrowth: calculateWeeklyGrowth(chartData)
    };
  }
};

/**
 * Get mock fanbase data for a brand based on its ID
 * Uses a simple hash to consistently assign the same scenario to the same brand
 * @param brandId - The brand ID
 * @param startDate - Start date for the data range
 * @param endDate - End date for the data range
 * @returns Mock fanbase data
 */
export const getMockFanbaseData = (
  brandId: number,
  startDate: string,
  endDate: string
): MockFanbaseData => {
  const scenarios = Object.values(MOCK_FANBASE_SCENARIOS);
  const scenarioIndex = brandId % scenarios.length;
  const selectedScenario = scenarios[scenarioIndex];
  
  return selectedScenario(startDate, endDate);
};

/**
 * Check if mock data should be used
 * This can be controlled via environment variables or feature flags
 */
export const shouldUseMockData = (): boolean => {
  // Check for development environment or mock flag
  return process.env.NODE_ENV !== 'production'
};